import React, { useState, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import CommunitySidebar from '../CommunitySidebar'
import useSetupMode from '../../hooks/useSetupMode'

/**
 * SetupModeShell — chrome for the FE-only Welcome / Book Activation Call
 * pages.
 *
 * Mirrors the live community portal's layout (page bg, sticky white
 * header, max-width, gradient wash) so /community/welcome feels like the
 * same product as /community/dashboard. Differences vs the live shell:
 *
 *   - Renders <CommunitySidebar mode="setup" /> instead of mode="live"
 *   - No "+ Create Game" button or notifications bell — the right side
 *     of the header is just a "Back to site" text link
 *   - No Supabase data fetching (no notifications, no community fetch);
 *     setup mode is sessionStorage-only
 *
 * Page title is derived from the URL so callers don't have to thread it
 * through. The legacy `active` prop is accepted but ignored.
 *
 * Today this shell is community-only (academy/venue use the same
 * /community/welcome surface in FE-only state); the persona prop is
 * preserved so the colleague's backend wiring can branch later.
 */

const SETUP_TAB_TITLES = {
  welcome: 'Welcome',
  'book-activation-call': 'Book activation call',
}

const PERSONA_BASE = {
  community: '/community',
  academy: '/academy',
  venue: '/venue-owner',
}

function deriveTabFromPath(pathname) {
  // /community/welcome → 'welcome'
  // /community/book-activation-call → 'welcome' (this page is a child of Welcome)
  // Any other /community/<tab> → '<tab>' (live portal route — sidebar will
  // show that tab as active even though the page itself is rendered by
  // the live CommunityLayout, not this shell)
  const last = pathname.split('/').filter(Boolean).pop() || 'welcome'
  if (last === 'book-activation-call') return 'welcome'
  return last
}

export default function SetupModeShell({ persona, children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const { persona: draftPersona } = useSetupMode()
  const effectivePersona = persona || draftPersona || 'community'
  const base = PERSONA_BASE[effectivePersona] || '/community'

  const activeTab = useMemo(() => deriveTabFromPath(location.pathname), [location.pathname])
  const pageTitle = SETUP_TAB_TITLES[activeTab] || 'Welcome'

  const handleTabChange = (tab) => {
    setIsSidebarOpen(false)
    navigate(`${base}/${tab}`)
  }

  const sidebarWidth = sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-[200px]'

  return (
    <div className="min-h-screen bg-[#F5F6F8] font-jakarta">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[55] lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden
        />
      )}

      <CommunitySidebar
        mode="setup"
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
      />

      <div className={`${sidebarWidth} min-h-screen relative transition-all duration-200`}>
        {/* Subtle brand gradient wash behind header area */}
        <div
          className="absolute inset-x-0 top-0 h-48 pointer-events-none z-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(99,102,241,0.06) 0%, rgba(99,102,241,0.02) 35%, transparent 100%)',
          }}
          aria-hidden="true"
        />

        {/* Sticky header — mirrors real portal's chrome.
            Setup state is communicated by the sidebar's Setup mode pill
            + locked tabs; no top banner needed. */}
        <header className="sticky top-0 z-40 lg:z-50 bg-white border-b border-slate-200">
          <div className="px-4 sm:px-6 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsSidebarOpen((v) => !v)}
                className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                aria-label="Toggle menu"
              >
                {isSidebarOpen ? <X className="h-5 w-5 text-slate-600" /> : <Menu className="h-5 w-5 text-slate-600" />}
              </button>
              <h1 className="text-base font-semibold text-slate-900">{pageTitle}</h1>
            </div>
          </div>
        </header>

        <main className="relative z-10">
          <div className="max-w-[1280px] mx-auto p-8">{children}</div>
        </main>
      </div>
    </div>
  )
}
